import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';

const PromoBanner = ({ currentUser, promoCode }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!promoCode || !promoCode.expires_at) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiryTime = new Date(promoCode.expires_at).getTime();
      const difference = expiryTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
        setIsExpired(false);
      } else {
        setIsExpired(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [promoCode]);

  if (!promoCode || !promoCode.code || isExpired) {
    return null;
  }

  return (
    <Row>
      <Col>
        <Card className="border-0 shadow-sm" style={{ 
          background: 'linear-gradient(135deg, #ff6666 0%, #ff6666 100%)',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <CardBody className="p-0">
            <div className="row g-0">
              <div className="col-lg-8">
                <div className="p-4 h-100 d-flex flex-column justify-content-center">
                  <div className="d-flex align-items-start mb-3">
                    <div className="me-3 pt-1 pr-3">
                      <div 
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{ 
                          width: '48px', 
                          height: '48px', 
                          backgroundColor: 'rgba(255,255,255,0.15)',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <i className="ti-gift text-white" style={{ fontSize: '1.5rem' }}></i>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h3 className="text-white mb-2 fw-bold" style={{ fontSize: '1.5rem' }}>
                        {t('general.promoBanner.exclusiveOfferFor')} <strong>{currentUser.first_name}</strong> ({promoCode.discount}% {t('general.promoBanner.off')})
                      </h3>
                      <p className="text-white mb-3" style={{ fontSize: '1rem', lineHeight: '1.4' }}>
                        {t('general.promoBanner.limitedTimePromoOffer')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="d-flex align-items-center flex-wrap">
                    <span className="text-white me-3 fw-medium" style={{ fontSize: '16px', fontWeight: 'bold' }}>{t('general.promoBanner.promoCode')}</span>
                    <div 
                      className="bg-white ml-3 px-4 py-2 rounded-pill fw-bold shadow-sm mr-5"
                      style={{ 
                        fontSize: '20px', 
                        letterSpacing: '1.5px',
                        color: '#000a60',
                        border: '2px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      {promoCode.code}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div 
                  className="p-4 h-100 d-flex flex-column justify-content-center"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                >
                    <div className="text-center mb-3">
                      <h6 className="text-white mb-0 fw-medium d-flex align-items-center justify-content-center" style={{ fontSize: '16px' }}>
                        <i className="ti-time me-2"></i>&nbsp;{t('general.promoBanner.expiresIn')}
                      </h6>
                    </div>
                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    {timeLeft.days > 0 && (
                      <div 
                        className="bg-white bg-opacity-20 rounded-3 p-3 m-1 text-center"
                        style={{ minWidth: '65px', backdropFilter: 'blur(10px)', color: '#ff6666', fontWeight: 'bold', border: '1px solid #ff6666' }}
                      >
                        <div className="fw-bold" style={{ fontSize: '1.4rem' }}>
                          {timeLeft.days}
                        </div>
                        <small className="text-white-75">{t('general.promoBanner.days')}</small>
                      </div>
                    )}
                    <div 
                      className="bg-white bg-opacity-20 rounded-3 p-3 m-1 text-center"
                      style={{ minWidth: '65px', backdropFilter: 'blur(10px)', color: '#ff6666', fontWeight: 'bold', border: '1px solid #ff6666' }}
                    >
                      <div className="fw-bold" style={{ fontSize: '1.4rem' }}>
                        {String(timeLeft.hours || 0).padStart(2, '0')}
                      </div>
                      <small className="text-white-75">{t('general.promoBanner.hours')}</small>
                    </div>
                    <div 
                      className="bg-white bg-opacity-20 rounded-3 p-3 m-1 text-center"
                      style={{ minWidth: '65px', backdropFilter: 'blur(10px)', color: '#ff6666', fontWeight: 'bold', border: '1px solid #ff6666' }}
                    >
                      <div className="fw-bold" style={{ fontSize: '1.4rem' }}>
                        {String(timeLeft.minutes || 0).padStart(2, '0')}
                      </div>
                      <small className="text-white-75">{t('general.promoBanner.minutes')}</small>
                    </div>
                    <div 
                      className="bg-white bg-opacity-20 rounded-3 p-3 m-1 text-center"
                      style={{ minWidth: '65px', backdropFilter: 'blur(10px)', color: '#ff6666', fontWeight: 'bold', border: '1px solid #ff6666' }}
                    >
                      <div className="fw-bold" style={{ fontSize: '1.4rem' }}>
                        {String(timeLeft.seconds || 0).padStart(2, '0')}
                      </div>
                      <small className="text-white-75">{t('general.promoBanner.seconds')}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div 
              className="px-4 py-3 text-center"
              style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}
            >
              <div className="d-flex align-items-center justify-content-center">
                <i className="ti-bolt text-white" style={{ fontSize: '1.1rem' }}></i>
                <span className="text-white" style={{ fontWeight: 'bold' }}>
                  {t('general.promoBanner.availableOnAllSubscriptionPlans')}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default PromoBanner;
